import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';

import { ajusteAntecipacaoApi } from '../../services/api/ajuste-antecipacao.api';
import {
  AjusteAntecipacao,
  MoedaAjuste,
  UpsertAjusteAntecipacaoPayload,
} from '../../types/ajuste-antecipacao';

export type AjusteMutationAction = 'upsert' | 'remove';

export interface AjusteAntecipacaoState {
  items: AjusteAntecipacao[];
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  currentClienteId: string | null;
  mutating: { moeda: MoedaAjuste; action: AjusteMutationAction } | null;
  mutationError: string | null;
}

const initialState: AjusteAntecipacaoState = {
  items: [],
  status: 'idle',
  error: null,
  currentClienteId: null,
  mutating: null,
  mutationError: null,
};

export const messageFromError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join('; ') : data.message;
    }
    if (error.response?.status === 401) return 'Sua sessão expirou.';
    if (error.response?.status === 403)
      return 'Você não tem permissão para acessar este recurso.';
    if (error.code === 'ERR_NETWORK')
      return 'Não foi possível conectar ao servidor.';
  }
  return fallback;
};

export const fetchAjustesAntecipacaoThunk = createAsyncThunk<
  AjusteAntecipacao[],
  string,
  { rejectValue: string }
>(
  'ajusteAntecipacao/fetch',
  async (clienteId, { rejectWithValue }) => {
    try {
      return await ajusteAntecipacaoApi.listByCliente(clienteId);
    } catch (e) {
      return rejectWithValue(messageFromError(e, 'Erro ao carregar ajustes'));
    }
  },
);

export const upsertAjusteAntecipacaoThunk = createAsyncThunk<
  AjusteAntecipacao,
  {
    clienteId: string;
    moeda: MoedaAjuste;
    payload: UpsertAjusteAntecipacaoPayload;
  },
  { rejectValue: string }
>(
  'ajusteAntecipacao/upsert',
  async ({ clienteId, moeda, payload }, { rejectWithValue }) => {
    try {
      return await ajusteAntecipacaoApi.upsert(clienteId, moeda, payload);
    } catch (e) {
      return rejectWithValue(messageFromError(e, 'Erro ao salvar ajuste'));
    }
  },
);

export const removeAjusteAntecipacaoThunk = createAsyncThunk<
  MoedaAjuste,
  { clienteId: string; moeda: MoedaAjuste },
  { rejectValue: string }
>(
  'ajusteAntecipacao/remove',
  async ({ clienteId, moeda }, { rejectWithValue }) => {
    try {
      await ajusteAntecipacaoApi.remove(clienteId, moeda);
      return moeda;
    } catch (e) {
      return rejectWithValue(messageFromError(e, 'Erro ao zerar ajuste'));
    }
  },
);

const slice = createSlice({
  name: 'ajusteAntecipacao',
  initialState,
  reducers: {
    setCurrentClienteId(state, action: PayloadAction<string | null>) {
      if (state.currentClienteId !== action.payload) {
        state.items = [];
        state.status = 'idle';
        state.error = null;
        state.mutating = null;
        state.mutationError = null;
      }
      state.currentClienteId = action.payload;
    },
    clearMutationError(state) {
      state.mutationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAjustesAntecipacaoThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAjustesAntecipacaoThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.items = action.payload;
      })
      .addCase(fetchAjustesAntecipacaoThunk.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Erro ao carregar ajustes';
      })

      .addCase(upsertAjusteAntecipacaoThunk.pending, (state, action) => {
        state.mutating = { moeda: action.meta.arg.moeda, action: 'upsert' };
        state.mutationError = null;
      })
      .addCase(upsertAjusteAntecipacaoThunk.fulfilled, (state, action) => {
        state.mutating = null;
        const idx = state.items.findIndex(
          (i) => i.moeda === action.payload.moeda,
        );
        if (idx >= 0) state.items[idx] = action.payload;
        else state.items.push(action.payload);
      })
      .addCase(upsertAjusteAntecipacaoThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Erro ao salvar ajuste';
      })

      .addCase(removeAjusteAntecipacaoThunk.pending, (state, action) => {
        state.mutating = { moeda: action.meta.arg.moeda, action: 'remove' };
        state.mutationError = null;
      })
      .addCase(removeAjusteAntecipacaoThunk.fulfilled, (state, action) => {
        state.mutating = null;
        state.items = state.items.filter((i) => i.moeda !== action.payload);
      })
      .addCase(removeAjusteAntecipacaoThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Erro ao zerar ajuste';
      });
  },
});

export const { setCurrentClienteId, clearMutationError } = slice.actions;

export default slice.reducer;
